import { memo, ReactElement } from 'react'
import { IMessage, Message } from '@weblite/ts-action/message'
import { t } from '$i18n'
import { useRoom } from '$logic/room/react-hook'
import { useHall, useRoomIsMookeb } from '$logic/hall/react-hook'
import { useSubscription } from '$logic/subscription/react-hook'
import { Message as MessageComponent } from '$common/chat-list-item-new/components/message'
import { useWapp } from '$logic/wapps/react-hook'
import { getImageUrl } from '$func/imageUrl'
import { useRoomLastMessage } from '$logic/room-last-message/react-hook'
import { HallAvatar } from '$common/avatar'
import { isCallStarted } from '$logic/call'
import { ChatListItemBase } from './chat-list-item-base'
import { ChatListItemSkeleton } from './chat-list-item-skeleton'
import { SenderIcon } from './components/sender-icon'
import { EventTitle } from './components/event-title'
import { useHallMessageAction } from './hooks/hall-chat-list-item-hooks'
import { ChatListItemProps } from './chat-list-item.types'
import {
  getLastMessageStatus,
  getLastMessageText,
} from './hooks/chat-list-item-hooks'
import { OpenStorySource } from '$logic/story/analytics'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { LastMessageSenderName } from '$common/chat-list-item-new/components/last-message-sender-name'

function WappImageGetter({ wappId }: { wappId: string }) {
  const { entity: wapp } = useWapp(wappId)
  return <SenderIcon Icon={getImageUrl(wapp?.image)} />
}

function LastMessageSenderIcon({
  isGroup,
  lastMessage,
}: {
  isGroup: boolean
  lastMessage?: Message
}): ReactElement | null {
  if (isGroup && lastMessage?.sender.type === 'WAPP')
    return <WappImageGetter wappId={lastMessage.sender.id} />

  return null
}
LastMessageSenderIcon.defaultProps = {
  lastMessage: undefined,
}

export const HallChatListItem = memo<ChatListItemProps>(
  function HallChatListItemComponent({
    chatId,
    pinned,
    isOnTop,
    selected,
    onClick,
    onContextMenu,
    onLongPress,
    isOpened,
    workspaceId,
    showWorkspaceBadge,
  }: ChatListItemProps) {
    const room = useRoom(chatId)

    const { entity: hall, loading: hallLoading } = useHall(chatId)
    const { roomIsMookeb } = useRoomIsMookeb(chatId)

    const { entity: subscription, loading: subscriptionLoading } =
      useSubscription(chatId)

    const isGroup = room?.type === 'GROUP'
    const isChannel = room?.type === 'CHANNEL'

    const messageAction = useHallMessageAction(isGroup, chatId)
    const lastMessage = useRoomLastMessage(chatId)

    const callStarted = room?.call?.startedAt && isCallStarted(room.call)
    const roomCallType = callStarted ? room?.call?.type : undefined
    const ongoingCallType =
      roomCallType !== 'call' && callStarted && !subscription?.suspended
        ? roomCallType
        : undefined
    const lastMessageIsJoinedChannel =
      isChannel &&
      (!lastMessage || lastMessage.date < (subscription?.createdAt || -1))

    if (hallLoading || subscriptionLoading) return <ChatListItemSkeleton />
    if (!hall || !subscription) return null

    const lastMessageSentWappName = getLastMessageText(lastMessage)

    const { unseen, seen, received, fromMe } = getLastMessageStatus(
      lastMessage,
      subscription.seen,
      room,
    )

    return (
      <ChatListItemBase
        chatId={chatId}
        selected={selected}
        roomType={room?.type}
        title={hall.name}
        verified={hall.verified}
        date={
          lastMessageIsJoinedChannel
            ? subscription.createdAt
            : lastMessage?.date || room?.createdAt
        }
        lastMessageSenderIcon={
          <LastMessageSenderIcon isGroup={isGroup} lastMessage={lastMessage} />
        }
        lastMessageSenderName={
          isGroup ? <LastMessageSenderName lastMessage={lastMessage} /> : null
        }
        lastMessageType={
          lastMessageIsJoinedChannel ? 'EVENT' : lastMessage?.type
        }
        lastMessageBody={
          lastMessageIsJoinedChannel ? '' : lastMessageSentWappName || ''
        }
        unseen={unseen}
        seen={isChannel || Boolean(seen)}
        received={Boolean(received)}
        muted={subscription.muted}
        fromMe={fromMe}
        pinned={pinned}
        eventTitle={
          lastMessageIsJoinedChannel ? (
            <MessageComponent event={t('chat.event.youJoinedChannel')} />
          ) : lastMessage?.type === 'EVENT' ? (
            <EventTitle lastMessage={lastMessage as IMessage} />
          ) : undefined
        }
        avatarElement={
          <HallAvatar
            source={OpenStorySource.ROOM_LIST}
            roomId={hall.roomId}
            checked={selected}
            ongoingCallType={ongoingCallType || undefined}
            size={AvatarSize.LG}
            withStory
            workspaceId={workspaceId}
            showWorkspaceBadge={showWorkspaceBadge}
          />
        }
        messageAction={messageAction}
        hasMarkAsUnread={subscription.unreadMark}
        hasMention={!!subscription.mentions?.length}
        isOnTop={isOnTop}
        isMookeb={roomIsMookeb}
        links={(lastMessage as IMessage)?.links}
        onClick={onClick}
        onContextMenu={onContextMenu}
        onLongPress={onLongPress}
        isOpened={isOpened}
        isGroup={isGroup}
      />
    )
  },
)
